import { useRef, useState, useEffect, useCallback } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import { ACTIONS, DIRECTION } from '../global-constants';

const VEHICLE_CARD_WIDTH = 242;

const isOverflown = ({
	clientWidth,
	clientHeight,
	scrollWidth,
	scrollHeight
}) => {
	return scrollHeight > clientHeight || scrollWidth > clientWidth;
};

const publishScrollEnd = (status) => {
	const pubsub = isBrowser && window?.DDC?.pubsub;
	if (pubsub) {
		return pubsub?.publish(ACTIONS.SCROLL_END, status);
	}
	return null;
};

const publishScrollStart = (status) => {
	const pubsub = isBrowser && window?.DDC?.pubsub;
	if (pubsub) {
		return pubsub?.publish(ACTIONS.SCROLL_START, status);
	}
	return null;
};

export default function useScroll() {
	const scrollAreaRef = useRef(null);
	const [canScroll, setCanScroll] = useState(true);
	const [isScrollEnd, setIsScrollEnd] = useState(false);
	const [isScrollStart, setIsScrollStart] = useState(true);
	const [compareItemWidth, setCompareItemWidth] =
		useState(VEHICLE_CARD_WIDTH);

	const onScroll = useCallback(
		(e) => {
			const isBottomReach =
				e.target.scrollLeft + e.target.offsetWidth >=
				e.target.scrollWidth - 40;

			if (isBrowser) {
				const stickyCardContainer = document?.querySelector(
					'.ws-vehicle-compare .sticky-card-container'
				);
				if (stickyCardContainer) {
					stickyCardContainer.scrollLeft = e.target.scrollLeft;
				}
			}
			// Check scroll to end
			if (isBottomReach) {
				publishScrollEnd(true);
			} else if (isScrollEnd && !isBottomReach) {
				publishScrollEnd(false);
			}

			// Check scroll at start
			if (e.target.scrollLeft === 0) {
				publishScrollStart(true);
			} else if (isScrollStart) {
				publishScrollStart(false);
			}
		},
		[isScrollEnd, isScrollStart]
	);

	const updateVehicleWidth = () => {
		if (!scrollAreaRef.current) {
			return;
		}

		const compareItem =
			scrollAreaRef.current.querySelector('.compare-item');
		if (compareItem) {
			setCompareItemWidth(compareItem?.clientWidth);
		}
	};

	const scrollTo = (direction) => {
		const toLeft = direction === DIRECTION.LEFT ? -1 : 1;
		const { scrollLeft, scrollWidth, offsetWidth } = scrollAreaRef.current;
		const offsetScroll = scrollLeft % compareItemWidth;
		const scrollLeftMax = scrollWidth - offsetWidth;
		let distanceToScroll = compareItemWidth - offsetScroll;

		if (direction === DIRECTION.LEFT) {
			distanceToScroll =
				offsetScroll === 0 ? compareItemWidth : offsetScroll;
		}

		let offsetX = toLeft * distanceToScroll;

		// scrollLeft after scroll
		const nextScrollLeft = scrollLeft + offsetX;

		// Limit scroll not out of screen on mobile
		// Swipe to right
		if (nextScrollLeft <= 0) {
			offsetX = toLeft * scrollLeft;
		}

		// Swipe to left
		if (nextScrollLeft >= scrollLeftMax) {
			offsetX = toLeft * (scrollLeftMax - scrollLeft);
		}

		if (offsetX) {
			scrollAreaRef.current.scrollBy(offsetX, 0);
		}
	};

	useEffect(() => {
		scrollAreaRef.current = document.querySelector('.compare-item-list');

		const updateScroll = () => {
			if (!scrollAreaRef.current) {
				return false;
			}

			const canScrollDetect = isOverflown(scrollAreaRef.current);
			setCanScroll(canScrollDetect);

			updateVehicleWidth();

			setIsScrollEnd(false);

			return true;
		};
		window.addEventListener('resize', updateScroll);
		updateScroll();

		return () => {
			window.removeEventListener('resize', updateScroll);
		};
	}, []);

	useEffect(() => {
		const subscriberStart = window?.DDC?.pubsub?.subscribe(
			ACTIONS.SCROLL_START,
			(status) => setIsScrollStart(status)
		);

		const subscriberEnd = window?.DDC?.pubsub?.subscribe(
			ACTIONS.SCROLL_END,
			(status) => setIsScrollEnd(status)
		);

		return () => {
			subscriberStart?.unsubscribe();
			subscriberEnd?.unsubscribe();
		};
	}, []);

	return {
		compareItemWidth,
		scrollAreaRef: scrollAreaRef.current,
		canScroll,
		isScrollEnd,
		isScrollStart,
		onScroll,
		scrollTo
	};
}
